import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { formatPhone } from '../lib/phone';
import ContactAccessPanel from '../components/ContactAccessPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Building2, User as UserIcon, Mail, Phone, MapPin, Edit2, Edit, Save,
  DollarSign, Receipt, Timer, ClipboardList, FolderOpen, FileText,
  MessageSquare, Bell, Plus, Trash2, CheckCircle2, Calendar, Users,
  ExternalLink, Settings2, Send, Loader2, Search, Pin, PinOff, Clock,
  Briefcase,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { HelpPanel } from '../components/ui/help-panel';
import { contactsHelp } from '../lib/helpContent';
import ContactFilesPanel from '../components/ContactFilesPanel';
import PinnedFolderChips from '../components/PinnedFolderChips';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const NOTE_TAG_OPTIONS = ['Meeting Notes', 'Tax Info', 'Follow-up', 'Billing Issue', 'General', 'Important'];
const PRIORITY_COLORS = { low: 'text-blue-600', normal: 'text-amber-600', high: 'text-red-600' };

// ─── Notes Tab ───
function NotesTab({ contactId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [newTags, setNewTags] = useState([]);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      let url = `${API_URL}/api/contacts/${contactId}/notes?sort=${sortOrder}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (tagFilter && tagFilter !== 'all') url += `&tag=${encodeURIComponent(tagFilter)}`;
      const res = await apiFetch(url);
      if (res.ok) setNotes(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [contactId, sortOrder, search, tagFilter]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/contacts/${contactId}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote, tags: newTags }),
      });
      if (res.ok) { setNewNote(''); setNewTags([]); fetchNotes(); toast.success('Note added'); }
    } catch { toast.error('Failed to add note'); }
    finally { setSubmitting(false); }
  };

  const togglePin = async (note) => {
    await apiFetch(`${API_URL}/api/contacts/${contactId}/notes/${note.note_id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !note.pinned }),
    });
    fetchNotes();
  };

  const deleteNote = async (noteId) => {
    await apiFetch(`${API_URL}/api/contacts/${contactId}/notes/${noteId}`, { method: 'DELETE' });
    fetchNotes();
    toast.success('Note deleted');
  };

  const toggleTag = (tag) => {
    setNewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  return (
    <div className="space-y-4" data-testid="notes-tab">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search notes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="notes-search" />
        </div>
        <Select value={tagFilter || 'all'} onValueChange={setTagFilter}>
          <SelectTrigger className="w-40" data-testid="notes-tag-filter">
            <SelectValue placeholder="All Tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {NOTE_TAG_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 p-4 rounded-lg border bg-muted/30">
        <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Write a note..." rows={3} data-testid="new-note-textarea" />
        <div className="flex flex-wrap gap-1.5">
          {NOTE_TAG_OPTIONS.map(tag => (
            <button key={tag} type="button" onClick={() => toggleTag(tag)} className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium border transition-colors",
              newTags.includes(tag) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/50"
            )} data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>
              {tag}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button onClick={handleAdd} disabled={!newNote.trim() || submitting} size="sm" data-testid="add-note-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Add Note
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[420px]">
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>No notes yet</p>
          </div>
        ) : (
          <div className="space-y-3 pr-2">
            {notes.map(note => (
              <div key={note.note_id} className={cn("rounded-lg border p-3 transition-colors", note.pinned && "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20")} data-testid={`note-${note.note_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm whitespace-pre-wrap flex-1">{note.note}</p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePin(note)} data-testid={`pin-note-${note.note_id}`}>
                      {note.pinned ? <PinOff className="w-3.5 h-3.5 text-amber-500" /> : <Pin className="w-3.5 h-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteNote(note.note_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {(note.tags || []).map(tag => (
                    <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                  ))}
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-auto">
                    <UserIcon className="w-3 h-3" />{note.created_by_name || 'Unknown'}
                    <Clock className="w-3 h-3 ml-1" />{new Date(note.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ─── People Tab ───
function PeopleTab({ contactId }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', title: '', email: '', phone: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const fetchPeople = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/contacts/${contactId}/people`);
      if (res.ok) setPeople(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const url = editing
        ? `${API_URL}/api/contacts/${contactId}/people/${editing.person_id}`
        : `${API_URL}/api/contacts/${contactId}/people`;
      const res = await apiFetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editing ? 'Person updated' : 'Person added');
        setShowForm(false);
        setEditing(null);
        setForm({ name: '', title: '', email: '', phone: '', is_primary: false });
        fetchPeople();
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (personId) => {
    await apiFetch(`${API_URL}/api/contacts/${contactId}/people/${personId}`, { method: 'DELETE' });
    fetchPeople();
    toast.success('Person removed');
  };

  const startEdit = (person) => {
    setEditing(person);
    setForm({ name: person.name, title: person.title || '', email: person.email || '', phone: person.phone || '', is_primary: person.is_primary });
    setShowForm(true);
  };

  if (loading) return <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div className="space-y-4" data-testid="people-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Manage contacts within this organization</p>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: '', title: '', email: '', phone: '', is_primary: false }); setShowForm(true); }} data-testid="add-person-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Person
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="John Smith" data-testid="person-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Title / Role</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="CFO, AP Manager..." data-testid="person-title" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" data-testid="person-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="person-phone" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_primary} onCheckedChange={v => setForm({ ...form, is_primary: v })} data-testid="person-primary" />
              <Label className="text-xs">Primary Contact</Label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-person-btn">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                {editing ? 'Update' : 'Add'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {people.length === 0 && !showForm ? (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No people added yet</p>
          <p className="text-xs mt-1">Add contacts within this organization</p>
        </div>
      ) : (
        <div className="space-y-2">
          {people.map(p => (
            <Card key={p.person_id}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{p.name}</span>
                    {p.is_primary && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Primary</Badge>}
                    {p.title && <span className="text-xs text-muted-foreground">- {p.title}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    {p.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                    {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{formatPhone(p.phone)}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p)}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.person_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Custom Fields Tab ───
function CustomFieldsTab({ contactId, workspaceId }) {
  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewField, setShowNewField] = useState(false);
  const [newField, setNewField] = useState({ label: '', field_type: 'text', options: '', required: false });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [defsRes, valsRes] = await Promise.all([
          apiFetch(`${API_URL}/api/workspaces/${workspaceId}/custom-fields`),
          apiFetch(`${API_URL}/api/contacts/${contactId}/custom-fields`),
        ]);
        if (defsRes.ok) setDefs(await defsRes.json());
        if (valsRes.ok) {
          const vals = await valsRes.json();
          const map = {};
          vals.forEach(v => { map[v.field_id] = v.value; });
          setValues(map);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [contactId, workspaceId]);

  const handleSaveValues = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/api/contacts/${contactId}/custom-fields`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: values }),
      });
      if (res.ok) toast.success('Custom fields saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleCreateField = async () => {
    if (!newField.label.trim()) { toast.error('Label required'); return; }
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/custom-fields`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newField,
          options: newField.field_type === 'dropdown' ? newField.options.split(',').map(s => s.trim()).filter(Boolean) : [],
        }),
      });
      if (res.ok) {
        const created = await res.json();
        setDefs(prev => [...prev, created]);
        setShowNewField(false);
        setNewField({ label: '', field_type: 'text', options: '', required: false });
        toast.success('Custom field created');
      }
    } catch { toast.error('Failed to create field'); }
  };

  const handleDeleteField = async (fieldId) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/custom-fields/${fieldId}`, { method: 'DELETE' });
    setDefs(prev => prev.filter(d => d.field_id !== fieldId));
    toast.success('Field deleted');
  };

  if (loading) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-4" data-testid="custom-fields-tab">
      {defs.length > 0 && (
        <div className="space-y-3">
          {defs.map(def => (
            <div key={def.field_id} className="flex items-center gap-3">
              <Label className="w-32 text-sm truncate flex-shrink-0">{def.label}{def.required && ' *'}</Label>
              {def.field_type === 'text' && (
                <Input value={values[def.field_id] || ''} onChange={e => setValues({ ...values, [def.field_id]: e.target.value })} className="flex-1" data-testid={`cf-${def.field_id}`} />
              )}
              {def.field_type === 'number' && (
                <Input type="number" value={values[def.field_id] || ''} onChange={e => setValues({ ...values, [def.field_id]: e.target.value })} className="flex-1" />
              )}
              {def.field_type === 'date' && (
                <Input type="date" value={values[def.field_id] || ''} onChange={e => setValues({ ...values, [def.field_id]: e.target.value })} className="flex-1" />
              )}
              {def.field_type === 'checkbox' && (
                <Switch checked={values[def.field_id] === true || values[def.field_id] === 'true'} onCheckedChange={v => setValues({ ...values, [def.field_id]: v })} />
              )}
              {def.field_type === 'dropdown' && (
                <Select value={values[def.field_id] || ''} onValueChange={v => setValues({ ...values, [def.field_id]: v })}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(def.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive flex-shrink-0" onClick={() => handleDeleteField(def.field_id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button onClick={handleSaveValues} disabled={saving} size="sm" data-testid="save-custom-fields-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Save Fields
          </Button>
        </div>
      )}

      {defs.length === 0 && !showNewField && (
        <div className="text-center py-8 text-muted-foreground">
          <Settings2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No custom fields defined</p>
          <p className="text-xs mt-1">Create fields to track additional info (Tax ID, Industry, etc.)</p>
        </div>
      )}

      {showNewField ? (
        <Card className="border-primary/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Field Label</Label>
                <Input value={newField.label} onChange={e => setNewField({ ...newField, label: e.target.value })} placeholder="e.g., Tax ID" data-testid="new-field-label" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={newField.field_type} onValueChange={v => setNewField({ ...newField, field_type: v })}>
                  <SelectTrigger data-testid="new-field-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    <SelectItem value="checkbox">Checkbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.field_type === 'dropdown' && (
              <div className="space-y-1">
                <Label className="text-xs">Options (comma-separated)</Label>
                <Input value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowNewField(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreateField} data-testid="create-field-btn">Create Field</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowNewField(true)} data-testid="add-custom-field-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Custom Field
        </Button>
      )}
    </div>
  );
}

// ─── Reminders Tab (workspace-level reminders linked to this contact) ───
function RemindersTab({ contactId, contactName, workspaceId }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ reminder_date: '', note: '', priority: 'normal', create_task: true });
  const [saving, setSaving] = useState(false);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/reminders?include_completed=true`);
      if (res.ok) {
        const all = await res.json();
        setReminders(all.filter(r => r.contact_id === contactId));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [contactId, workspaceId]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const handleCreate = async () => {
    if (!form.reminder_date || !form.note.trim()) { toast.error('Date and note required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/reminders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: contactId,
          reminder_date: form.reminder_date,
          note: form.note,
          priority: form.priority,
        }),
      });
      if (res.ok) {
        // Optionally also create a Task for stronger workflow
        if (form.create_task) {
          await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tasks`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: `Reminder: ${form.note}`,
              description: `Follow-up reminder for ${contactName || 'contact'}`,
              priority: form.priority === 'low' ? 'low' : (form.priority === 'high' ? 'high' : 'normal'),
              due_date: form.reminder_date,
              linked_contact_id: contactId,
            }),
          });
        }
        toast.success('Reminder set' + (form.create_task ? ' + task created' : ''));
        setShowForm(false);
        setForm({ reminder_date: '', note: '', priority: 'normal', create_task: true });
        fetchReminders();
      }
    } catch { toast.error('Failed to create reminder'); }
    finally { setSaving(false); }
  };

  const toggleComplete = async (rem) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/reminders/${rem.reminder_id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: !rem.completed }),
    });
    fetchReminders();
  };

  const deleteReminder = async (remId) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/reminders/${remId}`, { method: 'DELETE' });
    fetchReminders();
    toast.success('Reminder deleted');
  };

  if (loading) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-4" data-testid="reminders-tab">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium">Follow-up Reminders</p>
          <p className="text-xs text-muted-foreground">Quick date-based nudges. For full task workflow, use the Tasks tab.</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-reminder-btn">
          <Plus className="w-4 h-4 mr-1" /> New Reminder
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Follow-up Date *</Label>
                <Input type="date" value={form.reminder_date} onChange={e => setForm({ ...form, reminder_date: e.target.value })} data-testid="reminder-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="reminder-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note *</Label>
              <Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="What to follow up on..." rows={2} data-testid="reminder-note" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.create_task} onCheckedChange={v => setForm({ ...form, create_task: v })} data-testid="reminder-create-task" />
              <Label className="text-xs cursor-pointer">Also create a task in Task Manager</Label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="save-reminder-btn">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bell className="w-3 h-3 mr-1" />}
                Set Reminder
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {reminders.length === 0 && !showForm ? (
        <div className="text-center py-8 text-muted-foreground">
          <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No reminders set</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reminders.map(rem => {
            const isOverdue = !rem.completed && new Date(rem.reminder_date) < new Date();
            return (
              <Card key={rem.reminder_id} className={cn(rem.completed && "opacity-60")}>
                <CardContent className="p-3 flex items-center gap-3">
                  <button onClick={() => toggleComplete(rem)} className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                    rem.completed ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/40 hover:border-primary"
                  )} data-testid={`toggle-reminder-${rem.reminder_id}`}>
                    {rem.completed && <CheckCircle2 className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm", rem.completed && "line-through")}>{rem.note}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={cn("text-xs flex items-center gap-1", isOverdue ? "text-red-600 font-medium" : "text-muted-foreground")}>
                        <Calendar className="w-3 h-3" />{rem.reminder_date}
                        {isOverdue && ' (Overdue)'}
                      </span>
                      <Badge variant="outline" className={cn("text-[9px]", PRIORITY_COLORS[rem.priority])}>{rem.priority}</Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteReminder(rem.reminder_id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Financial Content ───
function FinancialContent({ data, contactType }) {
  const s = data.summary;
  const isCustomer = contactType === 'customer';

  return (
    <div className="space-y-5" data-testid="financial-360-content">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {isCustomer ? (
          <>
            <Card className="bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Invoiced</p><p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(s.invoices_total)}</p><p className="text-[10px] text-muted-foreground">{s.invoices_count} invoices</p></CardContent></Card>
            <Card className="bg-amber-50/50 dark:bg-amber-900/10 border-amber-200/50"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Outstanding</p><p className="text-xl font-bold text-amber-700 dark:text-amber-400">{fmt(s.invoices_outstanding)}</p></CardContent></Card>
          </>
        ) : (
          <>
            <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/50"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Total Billed</p><p className="text-xl font-bold text-blue-700 dark:text-blue-400">{fmt(s.bills_total)}</p><p className="text-[10px] text-muted-foreground">{s.bills_count} bills</p></CardContent></Card>
            <Card className="bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Paid</p><p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(s.bills_paid)}</p></CardContent></Card>
          </>
        )}
        <Card className="bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200/50"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Time</p><p className="text-xl font-bold text-indigo-700 dark:text-indigo-400">{s.time_total_hours}h</p><p className="text-[10px] text-muted-foreground">{s.time_unbilled_hours}h unbilled</p></CardContent></Card>
        <Card><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Tasks</p><p className="text-xl font-bold">{s.tasks_count}</p><p className="text-[10px] text-muted-foreground">{s.documents_count} docs</p></CardContent></Card>
      </div>

      {data.invoices?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Invoices ({data.invoices.length})</h4>
          <div className="rounded-lg border overflow-hidden"><Table>
            <TableHeader><TableRow className="bg-muted/30"><TableHead className="text-xs">Invoice</TableHead><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs text-right">Total</TableHead><TableHead className="text-xs text-right">Balance</TableHead></TableRow></TableHeader>
            <TableBody>{data.invoices.map(inv => (
              <TableRow key={inv.invoice_id}><TableCell className="text-xs font-medium"><Link to={`/invoices?preview=${inv.invoice_id}`} className="text-primary hover:underline">#{inv.invoice_no}</Link></TableCell><TableCell className="text-xs text-muted-foreground">{inv.invoice_date}</TableCell><TableCell><Badge variant="secondary" className="text-[10px] capitalize">{inv.status}</Badge></TableCell><TableCell className="text-xs text-right font-medium">{fmt(inv.total)}</TableCell><TableCell className={cn("text-xs text-right font-medium", inv.balance > 0 && "text-amber-600")}>{fmt(inv.balance)}</TableCell></TableRow>
            ))}</TableBody>
          </Table></div>
        </div>
      )}

      {data.bills?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Bills ({data.bills.length})</h4>
          <div className="rounded-lg border overflow-hidden"><Table>
            <TableHeader><TableRow className="bg-muted/30"><TableHead className="text-xs">Bill</TableHead><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs text-right">Total</TableHead><TableHead className="text-xs text-right">Balance</TableHead></TableRow></TableHeader>
            <TableBody>{data.bills.map(b => (
              <TableRow key={b.bill_id}><TableCell className="text-xs font-medium"><Link to={`/bills/${b.bill_id}`} className="text-primary hover:underline">#{b.bill_no}</Link></TableCell><TableCell className="text-xs text-muted-foreground">{b.bill_date}</TableCell><TableCell><Badge variant="secondary" className="text-[10px] capitalize">{b.status}</Badge></TableCell><TableCell className="text-xs text-right font-medium">{fmt(b.total)}</TableCell><TableCell className="text-xs text-right font-medium">{fmt(b.balance)}</TableCell></TableRow>
            ))}</TableBody>
          </Table></div>
        </div>
      )}

      {data.time_entries?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> Time Entries ({data.time_entries.length})</h4>
          <div className="rounded-lg border overflow-hidden"><Table>
            <TableHeader><TableRow className="bg-muted/30"><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Description</TableHead><TableHead className="text-xs">Staff</TableHead><TableHead className="text-xs text-right">Hours</TableHead><TableHead className="text-xs text-center">Billable</TableHead></TableRow></TableHeader>
            <TableBody>{data.time_entries.slice(0, 20).map(te => (
              <TableRow key={te.entry_id}><TableCell className="text-xs text-muted-foreground">{te.date}</TableCell><TableCell className="text-xs truncate max-w-[200px]">{te.description}</TableCell><TableCell className="text-xs text-muted-foreground">{te.user_name || '-'}</TableCell><TableCell className="text-xs text-right font-medium">{te.hours}h</TableCell><TableCell className="text-center">{te.billable ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <span className="text-xs text-muted-foreground">-</span>}</TableCell></TableRow>
            ))}</TableBody>
          </Table></div>
        </div>
      )}

      {data.documents?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><FolderOpen className="w-3.5 h-3.5" /> Documents ({data.documents.length})</h4>
          <div className="space-y-1">
            {data.documents.map(d => (
              <div key={d.document_id} className="flex items-center gap-2 p-2 rounded border bg-muted/20 text-xs">
                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="font-medium flex-1 truncate">{d.filename}</span>
                <span className="text-muted-foreground">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contact AI Documents Tab ───
function ContactDocumentsTab({ contactId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`${API_URL}/api/contacts/${contactId}/ai-documents`)
      .then(r => r.ok ? r.json() : [])
      .then(setDocs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [contactId]);

  if (loading) return <Skeleton className="h-32" />;

  if (docs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No AI documents attached yet</p>
        <p className="text-xs mt-1">Generate AI content in any hub and attach it to this contact</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="contact-documents-tab">
      {docs.map(d => (
        <Card key={d.document_id} className="hover:bg-muted/30 transition-colors">
          <CardContent className="p-3 flex items-start gap-3">
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{d.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{(d.content || '').slice(0, 200)}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <Badge variant="secondary" className="text-[9px] capitalize">{d.hub}</Badge>
                {d.doc_type && d.doc_type !== 'general' && <Badge variant="outline" className="text-[9px] capitalize">{d.doc_type}</Badge>}
                {(d.tags || []).map(t => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
                <span className="text-[10px] text-muted-foreground ml-auto">{new Date(d.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Main Contact Detail Page ───
export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data360, setData360] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const fetchContact = useCallback(async () => {
    if (!wsId || !contactId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/contacts/${contactId}?workspace_id=${wsId}`);
      if (res.ok) {
        const c = await res.json();
        setContact(c);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId, contactId]);

  const fetch360 = useCallback(async () => {
    if (!wsId || !contactId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/contacts/${contactId}/360`);
      if (res.ok) setData360(await res.json());
    } catch (e) { console.error(e); }
  }, [wsId, contactId]);

  const fetchTasks = useCallback(async () => {
    if (!wsId || !contactId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/tasks?contact_id=${contactId}`);
      if (res.ok) {
        const all = await res.json();
        setTasks(Array.isArray(all) ? all : all.tasks || []);
      }
    } catch {}
  }, [wsId, contactId]);

  useEffect(() => {
    fetchContact();
    fetch360();
    fetchTasks();
  }, [fetchContact, fetch360, fetchTasks]);

  const saveContact = async () => {
    // Editing is now done on /contacts/:id/edit page; this helper is kept as a fallback no-op.
    navigate(`/contacts/${contactId}/edit`);
  };

  const handleInviteToPortal = async () => {
    const emailToUse = inviteEmail.trim() || contact?.email;
    if (!emailToUse) { toast.error('Please enter an email address'); return; }
    setInviting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, contact_id: contactId, email: emailToUse }),
      });
      if (res.ok) {
        toast.success('Client invited to portal');
        setShowInvite(false);
      } else {
        const err = await res.json().catch(() => ({}));
        // If the client already has portal access, offer a password reset instead
        const alreadyHasAccess = res.status === 400 && /already.*portal.*access/i.test(err.detail || '');
        if (alreadyHasAccess && window.confirm("This client already has portal access. Send them a new temporary password instead?")) {
          const r2 = await apiFetch(`${API_URL}/api/client-portal/reset-password`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspace_id: wsId, contact_id: contactId }),
          });
          if (r2.ok) {
            const j2 = await r2.json();
            toast.success(j2.message || 'New password emailed to the client');
            setShowInvite(false);
          } else {
            const e2 = await r2.json().catch(() => ({}));
            toast.error(e2.detail || 'Could not reset password');
          }
        } else {
          toast.error(err.detail || 'Failed');
        }
      }
    } catch { toast.error('Failed to invite'); }
    finally { setInviting(false); }
  };

  if (loading) return <div className="space-y-4 p-6">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;
  if (!contact) return <div className="p-6 text-center text-muted-foreground">Contact not found</div>;

  const isCustomer = contact.contact_type === 'customer';
  const isOrg = contact.entity_type === 'organization';
  const s = data360?.summary || {};

  return (
    <div className="space-y-6" data-testid="contact-detail-page">
      {/* Header Ribbon */}
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/contacts')} data-testid="back-to-contacts">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center", isOrg ? "bg-blue-100 dark:bg-blue-900/30" : "bg-emerald-100 dark:bg-emerald-900/30")}>
              {isOrg ? <Building2 className="w-7 h-7 text-blue-600 dark:text-blue-400" /> : <UserIcon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{contact.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="secondary" className="text-xs capitalize">{contact.contact_type}</Badge>
                <Badge variant="outline" className="text-xs capitalize">{isOrg ? 'Organization' : 'Individual'}</Badge>
                {contact.status === 'inactive' && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                {contact.email && <span className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{contact.email}</span>}
                {(() => {
                  const primaryPhone = (contact.phones || []).find(p => p.is_primary) || (contact.phones || [])[0];
                  const phoneDisplay = primaryPhone?.number || contact.phone;
                  return phoneDisplay ? (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />{formatPhone(phoneDisplay)}
                      {primaryPhone?.type && <span className="capitalize text-[10px] opacity-70">({primaryPhone.type})</span>}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/contacts/${contactId}/edit`)} data-testid="edit-contact-btn">
              <Edit2 className="w-4 h-4 mr-1.5" /> Edit
            </Button>
            {isCustomer && !showInvite && (
              <Button variant="outline" size="sm" className="text-emerald-600" onClick={() => { setInviteEmail(contact.email || ''); setShowInvite(true); }} data-testid="invite-portal-btn">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Portal Invite
              </Button>
            )}
            <HelpPanel {...contactsHelp} />
          </div>
        </div>

        {/* Portal Invite inline */}
        {showInvite && (
          <div className="mt-4 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 flex items-center gap-2">
            <Input type="email" placeholder="client@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="flex-1" data-testid="portal-invite-email" />
            <Button size="sm" variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button size="sm" onClick={handleInviteToPortal} disabled={inviting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-portal-invite-btn">
              {inviting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Send
            </Button>
          </div>
        )}

        {/* Pinned folder chips — surface starred-for-this-contact folders */}
        <PinnedFolderChips contactId={contactId} workspaceId={wsId} />

        {/* Quick Metrics Ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {isCustomer ? (
            <>
              <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200/50">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Invoiced</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{fmt(s.invoices_total)}</p>
                <p className="text-[10px] text-muted-foreground">{s.invoices_count || 0} invoices</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Outstanding</p>
                <p className="text-lg font-bold text-amber-700 dark:text-amber-400">{fmt(s.invoices_outstanding)}</p>
              </div>
            </>
          ) : (
            <>
              <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200/50">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Billed</p>
                <p className="text-lg font-bold text-blue-700 dark:text-blue-400">{fmt(s.bills_total)}</p>
                <p className="text-[10px] text-muted-foreground">{s.bills_count || 0} bills</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200/50">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Paid</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{fmt(s.bills_paid)}</p>
              </div>
            </>
          )}
          <div className="p-3 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/10 border border-indigo-200/50">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Time Logged</p>
            <p className="text-lg font-bold text-indigo-700 dark:text-indigo-400">{s.time_total_hours || 0}h</p>
            <p className="text-[10px] text-muted-foreground">{s.time_unbilled_hours || 0}h unbilled</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 border">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Open Tasks</p>
            <p className="text-lg font-bold">{tasks.filter(t => t.status !== 'done').length}</p>
            <p className="text-[10px] text-muted-foreground">of {tasks.length} total</p>
          </div>
        </div>
      </div>

      {/* Main Layout: Sidebar + Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* Left Sidebar - Contact Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Contact Info</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="space-y-2.5">
                {contact.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}

                {/* Phones list */}
                {(contact.phones || []).length > 0 && (
                  <div className="space-y-1">
                    {(contact.phones || []).map((p) => (
                      <div key={p.phone_id} className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span>{formatPhone(p.number)}</span>
                        <Badge variant="outline" className="text-[9px] capitalize ml-auto">{p.type}</Badge>
                        {p.is_primary && <Badge className="text-[9px] bg-primary text-primary-foreground">Primary</Badge>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Addresses list */}
                {(contact.addresses || []).length > 0 && (
                  <div className="space-y-2">
                    {(contact.addresses || []).map((a) => {
                      const line = [a.line1, a.line2, a.city, [a.state, a.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                      return (
                        <div key={a.address_id} className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              <Badge variant="outline" className="text-[9px] capitalize">{a.type}</Badge>
                              {a.is_primary && <Badge className="text-[9px] bg-primary text-primary-foreground">Primary</Badge>}
                              {a.label && <span className="text-[10px] text-muted-foreground">{a.label}</span>}
                            </div>
                            <span className="text-xs">{line || '—'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {contact.payment_terms !== undefined && contact.payment_terms !== null && (
                  <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-muted-foreground" /><span>Net {contact.payment_terms}</span></div>
                )}
                {contact.credit_limit && (
                  <div className="flex items-center gap-2 text-sm"><Briefcase className="w-4 h-4 text-muted-foreground" /><span>Credit: {fmt(contact.credit_limit)}</span></div>
                )}
                <div className="text-[10px] text-muted-foreground pt-2 border-t mt-2">
                  Created: {new Date(contact.created_at).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Content - Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial">Financial</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks" className="flex items-center gap-1">
              Tasks {tasks.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{tasks.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">AI Documents</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">Files</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            {isOrg && <TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>}
            <TabsTrigger value="custom" data-testid="tab-custom">Custom Fields</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">Access</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isCustomer && data360?.invoices?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> Recent Invoices</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {data360.invoices.slice(0, 5).map(inv => (
                      <Link key={inv.invoice_id} to={`/invoices?preview=${inv.invoice_id}`} className="flex items-center justify-between px-4 py-2 border-b last:border-0 hover:bg-muted/50 text-sm">
                        <span>#{inv.invoice_no}</span>
                        <Badge variant="secondary" className="text-[10px] capitalize">{inv.status}</Badge>
                        <span className="font-medium">{fmt(inv.total)}</span>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}
              {!isCustomer && data360?.bills?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Receipt className="w-4 h-4" /> Recent Bills</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {data360.bills.slice(0, 5).map(b => (
                      <Link key={b.bill_id} to={`/bills/${b.bill_id}`} className="flex items-center justify-between px-4 py-2 border-b last:border-0 hover:bg-muted/50 text-sm">
                        <span>#{b.bill_no}</span>
                        <Badge variant="secondary" className="text-[10px] capitalize">{b.status}</Badge>
                        <span className="font-medium">{fmt(b.total)}</span>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}
              {tasks.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><ClipboardList className="w-4 h-4" /> Open Tasks</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {tasks.filter(t => t.status !== 'done').slice(0, 5).map(t => (
                      <div key={t.task_id} className="flex items-center justify-between px-4 py-2 border-b last:border-0 text-sm">
                        <span className="truncate flex-1">{t.title}</span>
                        <Badge variant="secondary" className="text-[10px] capitalize ml-2">{t.status?.replace('_', ' ')}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {data360?.time_entries?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Timer className="w-4 h-4" /> Recent Time ({s.time_total_hours}h)</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {data360.time_entries.slice(0, 5).map(te => (
                      <div key={te.entry_id} className="flex items-center justify-between px-4 py-2 border-b last:border-0 text-sm">
                        <span className="truncate flex-1 text-muted-foreground">{te.description}</span>
                        <span className="font-medium ml-2">{te.hours}h</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {(!data360 || ((data360.invoices?.length || 0) + (data360.bills?.length || 0) + (data360.time_entries?.length || 0) + tasks.length === 0)) && (
                <Card className="md:col-span-2">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No activity yet for this contact.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial">
            {data360 ? <FinancialContent data={data360} contactType={contact.contact_type} /> : <Skeleton className="h-48" />}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">{tasks.length} Tasks</h3>
                  <p className="text-xs text-muted-foreground">Trackable work items with status, priority & assignment.</p>
                </div>
                <Button size="sm" onClick={() => navigate(`/tasks/new?contactId=${contactId}&returnTo=${encodeURIComponent(`/contacts/${contactId}`)}`)} data-testid="new-contact-task-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> New Task
                </Button>
              </div>
              {tasks.length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground"><ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No tasks yet. Create one to track work for this {contact.contact_type}.</p></CardContent></Card>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader><TableRow className="bg-muted/30">
                      <TableHead className="text-xs">Title</TableHead>
                      <TableHead className="text-xs w-24">Status</TableHead>
                      <TableHead className="text-xs w-24">Priority</TableHead>
                      <TableHead className="text-xs w-28">Due Date</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {tasks.map(t => (
                        <TableRow key={t.task_id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate('/tasks')}>
                          <TableCell className="text-sm font-medium">{t.title}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-[10px] capitalize">{t.status?.replace('_', ' ')}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px] capitalize">{t.priority}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{t.due_date || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <RemindersTab contactId={contactId} contactName={contact.name} workspaceId={wsId} />
          </TabsContent>

          {/* AI Documents Tab */}
          <TabsContent value="documents">
            <ContactDocumentsTab contactId={contactId} />
          </TabsContent>

          {/* Files Tab — folder system synced with the Documents Hub */}
          <TabsContent value="files">
            <ContactFilesPanel
              workspaceId={contact?.workspace_id || wsId}
              contactId={contactId}
              contactType={contact?.contact_type || 'customer'}
            />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <NotesTab contactId={contactId} />
          </TabsContent>

          {/* People Tab (Org only) */}
          {isOrg && (
            <TabsContent value="people">
              <PeopleTab contactId={contactId} />
            </TabsContent>
          )}

          {/* Custom Fields Tab */}
          <TabsContent value="custom">
            <CustomFieldsTab contactId={contactId} workspaceId={wsId} />
          </TabsContent>

          {/* Access Tab */}
          <TabsContent value="access">
            <ContactAccessPanel contactId={contactId} contactName={contact?.name} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
